import BigNumber from 'bignumber.js';

export type FormatOptions = Partial<{
  decimalPlaces: number;
  roundingMode: BigNumber.RoundingMode;

  placeholder: string;
  zeroAsEmpty: boolean | string;
  sign: boolean;
  postfix: string;
  decimalPlacesPadding: boolean;
}> &
  BigNumber.Format;

const defaultConfig = {
  DECIMAL_PLACES: 18,
  ROUNDING_MODE: BigNumber.ROUND_HALF_UP,
  FORMAT: {
    groupSize: 3,
    groupSeparator: ',',
    decimalSeparator: '.',
  },
};

BigNumber.set(defaultConfig);

export const fillDecimals = (value: BigNumber.Value, length: number, fillString = '0') => {
  const [integerParts = '', decimalParts = ''] = value.toString().split('.');
  const paddedDecimalParts = decimalParts.padEnd(length, fillString);
  return `${integerParts}.${paddedDecimalParts}`.replace(/\.$/, '');
};

export const format = (
  n: BigNumber.Value | undefined | null,
  {
    decimalPlaces,
    roundingMode,
    sign = false,
    postfix = '',
    placeholder = '-',
    zeroAsEmpty = false,
    decimalPlacesPadding = false,
    ...formatOptions
  }: FormatOptions = {}
) => {
  if (n === undefined || n === null) return placeholder;
  const big = new BigNumber(n);

  const empty = big.isNaN() || (zeroAsEmpty && big.isZero());
  const emptyPlaceholder = typeof zeroAsEmpty === 'string' ? zeroAsEmpty : placeholder;
  if (empty) return emptyPlaceholder;

  const cut = typeof decimalPlaces === 'number' ? big.dp(decimalPlaces, roundingMode) : big;
  const formatted = cut.toFormat({
    ...defaultConfig.FORMAT,
    ...formatOptions,
  });
  const paddedFormatted =
    typeof decimalPlaces === 'number' && decimalPlacesPadding
      ? fillDecimals(formatted, decimalPlaces)
      : formatted;

  const posSign = sign && big.isPositive() ? '+' : '';
  return `${posSign}${paddedFormatted} ${postfix}`.trim();
};

export const formatDon = (n: BigNumber.Value | undefined | null) => format(n, { decimalPlaces: 0 });
export const formatCurrency = formatDon;
export const formatBoost = (value: BigNumber.Value, options: FormatOptions = {}) =>
  format(value, { decimalPlaces: 2, decimalPlacesPadding: true, ...options });

export const formatKaia = (value: BigNumber.Value, options: FormatOptions = {}) =>
  format(value, { decimalPlaces: 3, ...options });

const abbreviations = [
  { value: 1e18, unit: 'E' },
  { value: 1e15, unit: 'P' },
  { value: 1e12, unit: 'T' },
  { value: 1e9, unit: 'B' },
  { value: 1e6, unit: 'M' },
  { value: 1e3, unit: 'K' },
] as const;

export const abbreviate = (value?: number, options?: { decimalPlaces?: number }) => {
  if (value === undefined) return '';
  const { decimalPlaces = 2 } = options || {};
  const abs = Math.abs(value);
  const abbreviation = abbreviations.find((abbr) => abs >= abbr.value);
  if (!abbreviation) {
    return value.toString();
  }
  return (
    new BigNumber(value).dividedBy(abbreviation.value).toFixed(decimalPlaces) + abbreviation.unit
  );
};
